import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { searchAll } from "../../../services/searchServices";

import {
  addRecentSearch,
  clearRecentSearches,
  normalizeSearchResponse,
  readRecentSearches,
  readSearchCache,
  writeSearchCache,
} from "../utils/rechercheUtils";

import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_LIMIT,
  SEARCH_MIN_LENGTH,
  SEARCH_SECTIONS,
} from "../data/rechercheUiData";

export function useRecherchePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);

  const query = useMemo(() => {
    return String(searchParams.get("q") || "").trim();
  }, [searchParams]);

  const [term, setTerm] = useState(query);
  const [activeSection, setActiveSection] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [response, setResponse] = useState(null);
  const [recentSearches, setRecentSearches] = useState(() =>
    readRecentSearches()
  );

  useEffect(() => {
    setTerm(query);
  }, [query]);

  useEffect(() => {
    const next = term.trim();

    const timer = window.setTimeout(() => {
      if (!next) {
        if (query) {
          setSearchParams({}, { replace: true });
        }
        return;
      }

      if (next.length < SEARCH_MIN_LENGTH) return;

      if (next !== query) {
        setSearchParams({ q: next }, { replace: true });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, setSearchParams, term]);

  const runSearch = useCallback(
    async (nextQuery) => {
      const clean = String(nextQuery || "").trim();

      if (!clean || clean.length < SEARCH_MIN_LENGTH) {
        setResponse(null);
        setError("");
        setLoading(false);
        return;
      }

      const cached = readSearchCache(clean);
      if (cached) {
        setResponse(cached);
        setError("");
        setLoading(false);
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setLoading(true);
      setError("");

      try {
        const result = await searchAll({
          q: clean,
          limit: SEARCH_LIMIT,
        });

        if (requestIdRef.current !== requestId) return;

        setResponse(result);
        writeSearchCache(clean, result);
        setRecentSearches(addRecentSearch(clean));
      } catch (err) {
        console.error("Erreur recherche globale:", err);

        if (requestIdRef.current !== requestId) return;

        setResponse(null);
        setError("SEARCH_ERROR");
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    runSearch(query);
  }, [query, runSearch]);

  const normalized = useMemo(() => {
    if (!response) {
      return {
        raw: null,
        groups: SEARCH_SECTIONS.map((section) => ({
          ...section,
          items: [],
          count: 0,
        })),
        total: 0,
      };
    }

    return normalizeSearchResponse(response, SEARCH_SECTIONS);
  }, [response]);

  const submitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      const clean = term.trim();

      if (!clean) {
        setSearchParams({}, { replace: false });
        return;
      }

      if (clean.length < SEARCH_MIN_LENGTH) return;

      setSearchParams({ q: clean }, { replace: false });
    },
    [setSearchParams, term]
  );

  const clearSearch = useCallback(() => {
    setTerm("");
    setResponse(null);
    setError("");
    setActiveSection("all");
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  const selectRecentSearch = useCallback(
    (value) => {
      const clean = String(value || "").trim();
      if (!clean) return;

      setTerm(clean);
      setSearchParams({ q: clean }, { replace: false });
    },
    [setSearchParams]
  );

  const clearRecent = useCallback(() => {
    clearRecentSearches();
    setRecentSearches([]);
  }, []);

  const retry = useCallback(() => {
    runSearch(query);
  }, [query, runSearch]);

  const hasQuery = query.length >= SEARCH_MIN_LENGTH;
  const isTooShort = Boolean(term.trim()) && term.trim().length < SEARCH_MIN_LENGTH;

  return {
    query,
    term,
    setTerm,

    loading,
    error,
    hasQuery,
    isTooShort,

    normalized,
    activeSection,
    setActiveSection,

    recentSearches,

    submitSearch,
    clearSearch,
    selectRecentSearch,
    clearRecent,
    retry,
  };
}